import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, TrendingUp, Store, BarChart3, ShieldCheck, Users, Sprout, KeyRound, LogIn } from "lucide-react";
import PostLoginDashboard from "@/pages/PostLoginDashboard";
import LoginModal from "@/components/LoginModal";
import api from "@/lib/api";
import { DEMO_ENABLED } from "@/lib/demo";

const Home = () => {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [demoBusy, setDemoBusy] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState("/");

  // Developer demo bypass: /?demo=true → instantly instantiate mock admin session
  useEffect(() => {
    if (DEMO_ENABLED && params.get("demo") === "true" && !user) {
      (async () => {
        try {
          await api.post("/auth/demo-login");
          await refresh();
          navigate("/", { replace: true });
        } catch (e) {
          console.error("Demo login failed", e);
        }
      })();
    }
  }, [params, user, refresh, navigate]);

  const handleDemo = async () => {
    setDemoBusy(true);
    try {
      await api.post("/auth/demo-login");
      await refresh();
      setLoginOpen(false);
    } catch (e) {
      console.error("Demo login failed", e);
      setDemoBusy(false);
    }
  };

  // Logged-in users see the dashboard; public landing is untouched below.
  if (user) return <PostLoginDashboard />;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/15469881/pexels-photo-15469881.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600')",
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-gc px-4 sm:px-6 py-20 sm:py-28 lg:py-36 text-white">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/25 text-xs sm:text-sm font-medium backdrop-blur mb-6">
              <Sprout size={14} /> {t.home.hero_eyebrow}
            </div>
            <h1
              data-testid="hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight"
            >
              {t.home.hero_title}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl">
              {t.home.hero_sub}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/mandi" data-testid="hero-cta-mandi" className="btn-primary !bg-white !text-[#1E4D2B] hover:!bg-stone-100">
                {t.home.cta_explore} <ArrowRight size={18} />
              </Link>
              <Link
                to={user ? "/listings/new" : "/login"}
                data-testid="hero-cta-sell"
                className="btn-secondary"
              >
                {t.home.cta_sell} <ArrowRight size={18} />
              </Link>
            </div>
            {DEMO_ENABLED && (
              <button
                data-testid="hero-demo-btn"
                onClick={handleDemo}
                disabled={demoBusy}
                className="mt-4 inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium underline underline-offset-4 decoration-white/40 hover:decoration-white transition-colors"
              >
                <KeyRound size={14} />
                {demoBusy ? t.common.loading : "Try Demo Mode (Developer)"} →
              </button>
            )}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#F4F1EA] border-y border-stone-200">
        <div className="container-gc px-4 sm:px-6 py-10 grid grid-cols-3 gap-4 sm:gap-8 text-center">
          {[
            { v: "250+", l: t.home.stats_mandis },
            { v: "3,500+", l: t.home.stats_listings },
            { v: "18,000+", l: t.home.stats_farmers },
          ].map((s, i) => (
            <div key={i} data-testid={`stat-${i}`}>
              <div className="text-3xl sm:text-4xl font-bold text-[#1E4D2B]">{s.v}</div>
              <div className="mt-1 text-xs sm:text-sm text-stone-600 font-medium">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MODULES */}
      <section className="section-pad">
        <div className="container-gc">
          <div className="max-w-2xl mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900">
              {t.home.modules_title}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { to: "/mandi", icon: TrendingUp, title: t.home.m1_title, desc: t.home.m1_desc, color: "bg-[#1E4D2B]" },
              { to: "/trade", icon: Store, title: t.home.m2_title, desc: t.home.m2_desc, color: "bg-[#F59E0B]" },
              { to: "/analytics", icon: BarChart3, title: t.home.m3_title, desc: t.home.m3_desc, color: "bg-[#14361E]" },
            ].map((m, i) => {
              const Icon = m.icon;
              const handleClick = (e) => {
                if (!user) {
                  e.preventDefault();
                  setPendingRedirect(m.to);
                  setLoginOpen(true);
                }
              };
              return (
                <Link
                  key={i}
                  to={m.to}
                  onClick={handleClick}
                  data-testid={`module-card-${i}`}
                  className="card-gc p-6 hover:shadow-md transition-all hover:-translate-y-0.5 block"
                >
                  <div className={`w-12 h-12 rounded-xl ${m.color} text-white flex items-center justify-center mb-4`}>
                    <Icon size={22} />
                  </div>
                  <h3 className="text-xl font-semibold text-stone-900">{m.title}</h3>
                  <p className="mt-2 text-stone-600 leading-relaxed">{m.desc}</p>
                  <div data-testid={`module-open-${i}`} className="mt-4 inline-flex items-center gap-1 text-[#1E4D2B] font-semibold text-sm">
                    Open <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-white border-t border-stone-200 section-pad">
        <div className="container-gc grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900">
              {t.home.trust_title}
            </h2>
            <ul className="mt-6 space-y-4">
              {[
                { icon: Users, text: t.home.trust_1 },
                { icon: ShieldCheck, text: t.home.trust_2 },
                { icon: Sprout, text: t.home.trust_3 },
              ].map((it, idx) => {
                const Icon = it.icon;
                return (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#F4F1EA] text-[#1E4D2B] flex items-center justify-center flex-shrink-0">
                      <Icon size={18} />
                    </div>
                    <span className="text-stone-700 leading-relaxed pt-1.5">{it.text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="relative rounded-3xl overflow-hidden bg-stone-100 shadow-sm">
            <img
              data-testid="kota-garlic-image"
              src="https://images.unsplash.com/photo-1615477550927-6ec8da0b0e75?auto=format&fit=crop&w=1200&q=85"
              onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1768676124561-d14c038afbdb?auto=format&fit=crop&w=1200&q=85"; }}
              alt="Premium Kota Variety Lahsun — fresh large-sized garlic bulbs"
              className="w-full aspect-[4/3] object-cover"
              loading="lazy"
            />
            <div className="absolute top-3 left-3 sm:top-4 sm:left-4 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[10px] sm:text-xs font-bold uppercase tracking-wider text-[#1E4D2B]">
              Premium · Kota Variety
            </div>
          </div>
        </div>
        {/* Login CTA below image */}
        {!user && (
          <div className="container-gc mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <p className="text-stone-700 text-sm sm:text-base text-center sm:text-right">
              Ready to access live mandi rates & premium features?
            </p>
            <button
              data-testid="trust-login-cta"
              onClick={() => { setPendingRedirect("/"); setLoginOpen(true); }}
              className="btn-primary !h-11 !px-5"
            >
              <LogIn size={16} /> {t.nav.login}
            </button>
          </div>
        )}
      </section>

      {/* FOOTER is rendered globally by App.js */}
      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        redirectTo={pendingRedirect}
        onDemo={DEMO_ENABLED ? handleDemo : undefined}
      />
    </div>
  );
};

export default Home;
